import { motion } from "framer-motion";
import { Plus, Mail, Coffee } from "lucide-react";
import { Helmet } from "react-helmet-async";
import logo from "@/assets/logo.webp";
import phoneMockup from "@/assets/phone-mockup.webp";
import laptopMockup from "@/assets/laptop-mockup.webp";
import googlePlayBadge from "@/assets/Google_Play.png";
import appStoreBadge from "@/assets/App_Store.png";

const Home = () => {

  return (
    <>
      <Helmet>
        <title>TodoTomorrow - Capture on the go. Delivered to your inbox.</title>
        <meta name="description" content="Capture tasks anytime, anywhere. Get them delivered to your inbox every morning. One email. Free during launch. No app switching, no forgotten tasks." />
        <meta name="keywords" content="todo app, task management, email productivity, morning routine, brain dump, task capture, productivity tool" />
        <meta name="author" content="TodoTomorrow" />
        <link rel="canonical" href="https://www.todotomorrow.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="TodoTomorrow - Capture on the go. Delivered to your inbox." />
        <meta property="og:description" content="Capture tasks anytime, anywhere. Get them delivered to your inbox every morning. One email. Free during launch. No app switching, no forgotten tasks." />
        <meta property="og:url" content="https://www.todotomorrow.com/" />
        
        {/* Twitter */}
        <meta name="twitter:title" content="TodoTomorrow - Capture on the go. Delivered to your inbox." />
        <meta name="twitter:description" content="Capture tasks anytime, anywhere. Get them delivered to your inbox every morning. One email. Free during launch. No app switching, no forgotten tasks." />
      </Helmet>
      <main className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-12"
          >
            <img src={logo} alt="TodoTomorrow" className="w-20 h-20" width="140" height="140" />
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl md:text-6xl font-bold text-foreground text-center mb-6 text-balance leading-tight"
          >
            Capture on the go.<br />Delivered to your inbox.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground text-center mb-12 max-w-2xl mx-auto text-balance"
          >
            Tasks emailed to you every morning. One email. Free during launch.
          </motion.p>

          {/* Download Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex justify-center items-center gap-3 mb-20"
          >
            <a 
              href="https://play.google.com/store/apps/details?id=com.todotomorrow.app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <img 
                src={googlePlayBadge} 
                alt="Get it on Google Play" 
                className="h-[60px]"
              />
            </a>
            <a 
              href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <img 
                src={appStoreBadge} 
                alt="Download on the App Store" 
                className="h-[60px]"
              />
            </a>
          </motion.div>

          {/* Device Mockups */}
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            {/* Phone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="relative max-w-xs mx-auto"
            >
              <img 
                src={phoneMockup} 
                alt="TodoTomorrow mobile app showing task list" 
                className="w-full h-auto drop-shadow-2xl"
                width="525"
                height="993"
                fetchPriority="high"
              />
            </motion.div>

            {/* Laptop Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="relative"
            >
              <img 
                src={laptopMockup} 
                alt="TodoTomorrow email in inbox showing morning task list" 
                className="w-full h-auto drop-shadow-2xl"
                width="1258"
                height="844"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-secondary">
        <div className="container mx-auto max-w-5xl">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-4xl font-semibold text-center mb-4"
          >
            How It Works
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-center mb-20"
          >
            Three simple steps to a clearer mind
          </motion.p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12 relative">
            {/* Connecting lines for desktop */}
            <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 bg-border/40" style={{ transform: 'translateX(-50%)' }} />
            <div className="hidden md:block absolute top-10 left-2/3 right-0 h-0.5 bg-border/40" style={{ transform: 'translateX(-50%)' }} />
            
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="flex flex-col items-center text-center relative z-10"
            >
              <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-background">
                <Plus className="w-10 h-10 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Capture anytime</h3>
              <p className="text-muted-foreground">Add tasks on the go, during commutes, between meetings, or whenever ideas strike.</p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col items-center text-center relative z-10"
            >
              <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-background">
                <Mail className="w-10 h-10 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Email delivered</h3>
              <p className="text-muted-foreground">Wake up to your tasks in your inbox, ready to tackle. Morning delivery, every day.</p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col items-center text-center relative z-10"
            >
              <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-background">
                <Coffee className="w-10 h-10 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Start your day</h3>
              <p className="text-muted-foreground">Simple. No app switching. Just clarity. Get it done where you already work.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How I Use TodoTomorrow */}
      <section className="py-24 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl font-semibold text-foreground mb-8">
              How I Use TodoTomorrow
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed text-left">
              <p>
                I capture tasks everywhere I go - on long drives, during my commute, between meetings, while listening to podcasts. Ideas hit at the worst times, and I need to catch them fast.
              </p>
              
              <p>
                I tried every todo app out there. The problem? I'd forget to check them. They just sat there, unopened, while I worked in my email all day.
              </p>
              
              <p>
                That's why TodoTomorrow works for me. I capture whenever inspiration strikes, then my tasks show up in my inbox the next morning - right where I'm already working. One place to review everything. No app switching. No forgotten tasks.
              </p>
              
              <p>
                My mind stays clear, and my inbox stays organized.
              </p>
              
              <p className="text-foreground font-medium pt-4">
                — Joe G., creator of TodoTomorrow
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-24 px-6 bg-secondary">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {/* Testimonial 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 rounded-2xl bg-secondary"
            >
              <p className="text-lg text-foreground mb-6 italic leading-relaxed">
                "I was on a 2-hour drive when podcast ideas started flowing. TodoTomorrow let me capture them instantly. Got the email in the morning and started working on the best ones right away."
              </p>
              <p className="text-sm text-muted-foreground">— Sarah K., Product Manager</p>
            </motion.div>

            {/* Testimonial 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-8 rounded-2xl bg-secondary"
            >
              <p className="text-lg text-foreground mb-6 italic leading-relaxed">
                "Between meetings, ideas used to slip away. Now I capture everything without breaking my flow. Morning email gives me a clear picture of what matters."
              </p>
              <p className="text-sm text-muted-foreground">— Michael R., Designer</p>
            </motion.div>

            {/* Testimonial 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-8 rounded-2xl bg-secondary"
            >
              <p className="text-lg text-foreground mb-6 italic leading-relaxed">
                "Email is my main tool. Getting yesterday's scattered thoughts organized into one morning email is perfect. I start my day knowing exactly what I committed to."
              </p>
              <p className="text-sm text-muted-foreground">— James T., Entrepreneur</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-6xl font-bold text-foreground mb-4">Free</h2>
            <p className="text-xl text-muted-foreground mb-6">Limited time launch</p>
            <p className="text-base text-muted-foreground mb-12 max-w-md mx-auto">
              Get in early. No credit card. Help us shape the product.
            </p>
            
            <div className="flex justify-center items-center gap-3">
              <a 
                href="https://play.google.com/store/apps/details?id=com.todotomorrow.app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
              >
                <img 
                  src={googlePlayBadge} 
                  alt="Get it on Google Play" 
                  className="h-[60px]"
                />
              </a>
              <a 
                href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
              >
                <img 
                  src={appStoreBadge} 
                  alt="Download on the App Store" 
                  className="h-[60px]"
                />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Home;